import React, { FunctionComponent, SVGProps, useEffect, useMemo, useState } from 'react';
import { faPlusSquare } from '@fortawesome/fontawesome-free-solid';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink as Link, useLocation } from 'react-router-dom';
import { useRedux } from '../../../states/redux-state';

import {
  getFavoriteLinksSelector,
  iFav,
} from '../../../shared/db/favorites-selectors';
import { iFullStoreState } from '../../../shared/interfaces';
import DashboardIcon from '../../SVG-components/dashboard';
import DeviceIcon from '../../SVG-components/device';
import LayersIcon from '../../SVG-components/layers';
import ListAltIcon from '../../SVG-components/list_alt';
import PeopleIcon from '../../SVG-components/people';

import { ReactComponent as MaintenanceIcon } from '../../../assets/svg/maintanance.svg';

import { logout as logoutAPI } from '../../../api';
import useAutoOpenDashboardIfClosed from '../../../hooks/auto-open-dashboard-if-closed';
import { ACL, UserCan } from '../../../shared/constants';
import { fbapp } from '../../../shared/firebase';
import { resetRememberMeIfExists } from "../../../shared/helpers";
import { localStorage } from '../../../shared/storage';
import { Actions as GeneralActions } from '../../../stores/reducers/general-reducers';
import Portal from '../../Portal';
import SvgIcon from '../../SvgIcon/SvgIcon';
import IgnitionStatusList from '../../IgnitionStatusList';
import { Fa } from '../../elements/fa';
import './styles.scss';
import { scrollContainerEl } from '../../DashboardBlock';

type PageType = {
  enabledForDriver?: boolean;
  name: string;
  link: string;
  Icon: (() => JSX.Element) | FunctionComponent<SVGProps<SVGSVGElement>>;
};
const PAGES: PageType[] = [
  {
    name: 'Dashboard',
    link: '/dashboard',
    Icon: DashboardIcon,
  },
  {
    name: process.env.REACT_APP_DEVICES_TAB_NAME || 'Devices',
    link: '/device',
    Icon: DeviceIcon,
    enabledForDriver: true,
  },
  {
    name: process.env.REACT_APP_PEOPLE_TAB_NAME || 'People',
    link: '/person',
    Icon: PeopleIcon,
    enabledForDriver: true,
  },
  {
    name: process.env.REACT_APP_REPORTS_TAB_NAME || 'Reports',
    link: '/reports',
    Icon: ListAltIcon,
  },
  {
    name: process.env.REACT_APP_TAGS_TAB_NAME || 'Tags',
    link: '/tags',
    Icon: LayersIcon,
  },
//  {
//    name: process.env.REACT_APP_BILLING_TAB_NAME|| 'Billing',
//    link: '/billing',
//    Icon: Billing,
//  },
  {
    name: process.env.REACT_APP_MAINTENANCE_TAB_NAME || 'Maintenance',
    link: '/maintenance',
    Icon: MaintenanceIcon,
  },
];

const MainMenu = () => {
  const [colors, setColors] = useState({
    colorDark: null,
    colorBlack: null,
    colorGunPowder: null,
    colorWhite: null,
    colorTangerine: null,
    colorEclipse: null,
    colorSnow: null,
    colorSuvaGrey: null,
  });

  const dispatch = useDispatch();
  const isSearchVis = useSelector<iFullStoreState, boolean>(s => s.general.showSearch);

  const [logo, setLogo] = useState(null);
  const userCanDo = useSelector<iFullStoreState, Array<string>>(
    (s) => s.auth.user?.acl?.can
  );
  const isDisabled = useSelector<iFullStoreState, boolean>(
    (s) => {
      const userId = s.auth.user.uid;

      const person = Object.values(s.general.people).find(v => v.id === userId);

      return person?.disabled ?? false;
    }
  )
  const hideTags = !ACL.check(UserCan.SEE_TAGS, userCanDo, true);
  const hideMaintenance = !ACL.check(UserCan.SET_MAINTENANCE, userCanDo, true);
  const hideDashboard = ACL.check(UserCan.HIDE_DASHBOARD_PAGE, userCanDo, true);
  const isDriver = useRedux((s) => s.auth.isDriver);
  const location = useLocation();
  const isDevicePage = location.pathname === '/device';

  const filterPage = (page: PageType) => {
    if (isDriver && !page.enabledForDriver) return false;
    if (hideMaintenance && page.name === 'Maintenance') return false;
    if (hideTags && page.name === 'Tags') return false;
    if (hideDashboard && page.name === 'Dashboard') return false;

    // opened by default
    return true;
  };

  const showDashboardIconTitles = process.env.REACT_APP_SHOW_TITLES_DASHBOARD === 'true';
  const getPages = () => PAGES.filter(filterPage);

  useEffect(() => {
    let url = new URL(window.location.href);
    let params = new URLSearchParams(url.search);
    let localColors = {};
    Object.keys(colors).map((item) => {
      if (url.searchParams.get(item)) {
        localStorage.set(item, url.searchParams.get(item));
        localColors[item] = localStorage.get(item);
        params.delete(item);
        window.history.replaceState({}, '', `${url.pathname}?${params}`);
      }

      if (localStorage.get(item) && !url.searchParams.get(item)) {
        localColors[item] = localStorage.get(item);
      }
    });

    if (url.searchParams.get('logo')) {
      localStorage.set(
        'logo',
        decodeURIComponent(url.searchParams.get('logo'))
      );
      setLogo(localStorage.get('logo'));
      params.delete('logo');
      window.history.replaceState({}, '', `${url.pathname}?${params}`);
    }

    if (localStorage.get('logo') && !url.searchParams.get('logo')) {
      setLogo(localStorage.get('logo'));
    }

    setColors({
      ...colors,
      ...localColors,
    });
  }, []);

  const css = `
            :root {
                 ${
                   colors.colorDark
                     ? `--colorDark: #` + colors.colorDark + `;`
                     : ``
                 }
                 ${
                   colors.colorBlack
                     ? `--colorBlack: #` + colors.colorBlack + `;`
                     : ``
                 }
                 ${
                   colors.colorGunPowder
                     ? `--colorGunPowder: #` + colors.colorGunPowder + `;`
                     : ``
                 }
                 ${
                   colors.colorWhite
                     ? `--colorWhite: #` + colors.colorWhite + `;`
                     : ``
                 }
                 ${
                   colors.colorTangerine
                     ? `--colorTangerine: #` + colors.colorTangerine + `;`
                     : ``
                 }
                 ${
                   colors.colorEclipse
                     ? `--colorEclipse: #` + colors.colorEclipse + `;`
                     : ``
                 }
                 ${
                   colors.colorSnow
                     ? `--colorSnow: #` + colors.colorSnow + `;`
                     : ``
                 }
                 ${
                   colors.colorSuvaGrey
                     ? `--colorSuvaGrey: #` + colors.colorSuvaGrey + `;`
                     : ``
                 }
            }
        `;

  const autoOpenDashboardIfClosed = useAutoOpenDashboardIfClosed();

  const handleLinkClick = () => {
    autoOpenDashboardIfClosed();
  };

  const logout = (e) => {
      e.preventDefault();
      logoutAPI().finally(() => {
          resetRememberMeIfExists();
          document.location.href = '/';
          fbapp.auth().signOut();
      });
  };

  if (isDisabled) {
    return (
      <Portal>
        <div className="portal-container">
          <div className="portal-container__modal">
            <p>Your account is disabled.</p>
            <p>Please, contact to organization owner.</p>
            <input type="button" value="Ok" onClick={(e) => logout(e)} />
          </div>
        </div>
      </Portal>
    )
  }

  const hideSearch = () => {
    if (isSearchVis) {
      dispatch(GeneralActions.HIDE_SEARCH);
      dispatch(GeneralActions.CHANGE_TERM(''));
    }
    dispatch(GeneralActions.HIDE_BOOKMARKS_SEARCH);
    dispatch(GeneralActions.CHANGE_SEARCH_BY(''));
    scrollContainerEl?.scrollTo?.({ top: 0 });
  };

  return (
    <div className='menu-block' onClick={hideSearch}>
      {colors && <style>{css}</style>}
      <div className='logo'>
        {/* {logo && <img src={logo} alt="logo"/>
                {!logo && <MasTrackLogoIcon/>} */}
        <img src={process.env.REACT_APP_ICON} alt='logo' />
      </div>
      <div className='main-menu'>
        {getPages().map(({ name, link, Icon }) => (
          <Link key={name} to={link} onClick={handleLinkClick}>
            <Icon />
            <span style={showDashboardIconTitles ? {visibility: 'visible', opacity: 1} : {}}>{name}</span>
          </Link>
        ))}
        {/* Horizontal separator */}
        <div style={{ borderTop: '1px solid #444', margin: '14px 0', width: '100%' }} />
        {/* Notes Search: available to all users */}
        <div className="sidebar-link-group">
          <Link
            to="#"
            className="add-vehicle-link notes-search-link"
            isActive={() => false}
            onClick={e => {
              e.preventDefault();
              // Open the Notes Search modal via a custom event or global handler
              const evt = new CustomEvent('openNotesSearchModal', {
                detail: { fromSidebarNav: true }
              });
              window.dispatchEvent(evt);
            }}
          >
            <span style={{ color: 'orange' }}>
              Notes Search
            </span>
          </Link>
          {ACL.check(UserCan.DO_ANYTHING, userCanDo) && (
            <Link
              to="#"
              isActive={() => false}
              className="add-vehicle-link"
              style={{marginTop: 4}}
              onClick={e => {
                e.preventDefault();
                const evt = new CustomEvent('openAddInventoryDeviceModal');
                window.dispatchEvent(evt);
              }}
            >
              <span>
                <Fa icon={faPlusSquare} style={{ marginRight: 4 }} />
                Vehicle
              </span>
            </Link>
          )}
        </div>
      </div>
      <div className='favorites' style={{marginBottom: isDevicePage ? 170 : 0}}>
        <FavoriteList
          onClick={() => {
            // TODO logic for open dashboard
          }}
        />
      </div>
      {isDevicePage && (
        <div className='menu-ignition-legend'>
          <IgnitionStatusList />
        </div>
      )}
      <div className='all-favorites'>
        <Link to='/favorites'>All Favorites</Link>
      </div>
    </div>
  );
};

export default MainMenu;

const FavoriteList = ({ onClick }: { onClick: () => void }) => {
  const favorites = useSelector<iFullStoreState, Array<iFav>>(
    getFavoriteLinksSelector()
  );
  const favoritesJsx = useMemo(
    () =>
      favorites.map((fav) => (
        <StandardFav
          onClick={onClick}
          key={fav.itemId}
          id={fav.itemId}
          fav={fav}
          color={fav?.color}
        />
      )),
    [favorites]
  );
  return <>{favoritesJsx}</>;
};

const StandardFav = ({ fav, color }: { id: string; fav: iFav; onClick: any, color: any }) => {
  return (
    // TODO logic for open dashboard
    <Link to={fav.link}>
      {fav.icon ? (
        // @ts-ignore
        (<div className='ico-container'>
          <SvgIcon icon={fav.icon} color={color} />
        </div>)
      ) : fav.photo ? (
        <img src={fav.photo} alt='logo' />
      ) : (
        <div className='ico-container'></div>
      )}
      <span>{fav.name}</span>
    </Link>
  );
};
